import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import MarketingReveal from './MarketingReveal';
import { MADE_ON_VENDALIST } from './homeDummyContent';

/**
 * Peerspace “Made in …” horizontal gallery — photo-led proof with dummy listings.
 */
const MarketingMadeBand = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, []);

  const scrollBy = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  return (
    <section className="border-b border-m-border-light bg-m-surface py-20 sm:py-28">
      <MarketingReveal className="page-container">
        <div className="mb-10 flex flex-col gap-4 sm:mb-14 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-m-ink-faint">
              Made on VendaList
            </p>
            <h2 className="marketing-section-title mb-3 text-m-ink">
              From indie nights to boardroom launches
            </h2>
            <p className="marketing-body-lg text-m-ink-muted">
              Remarkable things are booked here every week — booths, crews, and
              creatives that make the night feel intentional.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              aria-label="Scroll left"
              disabled={!canScrollLeft}
              onClick={() => scrollBy(-1)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-m-border-light text-m-ink transition-opacity disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Scroll right"
              disabled={!canScrollRight}
              onClick={() => scrollBy(1)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-m-border-light text-m-ink transition-opacity disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide sm:gap-5"
        >
          {MADE_ON_VENDALIST.map((item) => (
            <Link
              key={item.id}
              to={item.href}
              className="group w-[78vw] max-w-sm shrink-0 sm:w-[320px]"
            >
              <div className="relative mb-3 overflow-hidden rounded-2xl">
                <img
                  src={item.image}
                  alt=""
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-80" />
              </div>
              <h3 className="mb-1 text-base font-medium tracking-tight text-m-ink">
                {item.title}
              </h3>
              <p className="text-sm text-m-ink-muted">{item.meta}</p>
            </Link>
          ))}
        </div>
      </MarketingReveal>
    </section>
  );
};

export default MarketingMadeBand;
